import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dialog } from '../ui/components';
import { Icon } from '../ui/icons';
import { useI18n } from '../i18n';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const MIN_CHECK_GAP_MS = 60 * 1000;
// After "Later", leave the user alone for a while before nudging again.
const RENOTIFY_AFTER_MS = 60 * 60 * 1000;
// Last version whose release notes the user has already seen (across sessions).
const SEEN_VERSION_KEY = 'pp.seenVersion';

async function fetchVersion() {
  const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) return null;
  const data = await res.json();
  return data && typeof data.version === 'string' && data.version ? data : null;
}

// A localized field is either a plain string or {en: "...", id: "..."}.
function pickText(field, lang) {
  if (typeof field === 'string') return field.trim() || null;
  if (field && typeof field === 'object') {
    const v = field[lang] || field.en;
    return typeof v === 'string' && v.trim() ? v.trim() : null;
  }
  return null;
}

// Notes are curated {title, description} entries (localized); bare strings are tolerated.
function cleanNotes(notes) {
  if (!Array.isArray(notes)) return [];
  return notes
    .map((n) => {
      if (typeof n === 'string' && n.trim()) return { title: n.trim(), description: null };
      if (n && pickText(n.title, 'en')) return { title: n.title, description: n.description ?? null };
      return null;
    })
    .filter(Boolean);
}

function ReleaseNotes({ notes, lang }) {
  return (
    <div className="pp-vu__list">
      {notes.slice(0, 12).map((note, i) => {
        const title = pickText(note.title, lang);
        const description = pickText(note.description, lang);
        if (!title) return null;
        return (
          <div className="pp-vu__item" key={i}>
            <span className="pp-vu__item-icon"><Icon.Check size={14} /></span>
            <div className="pp-vu__item-body">
              <div className="pp-vu__item-title">{title}</div>
              {description && <div className="pp-vu__item-desc">{description}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Hero({ t, lang, builtAt }) {
  return (
    <div className="pp-vu__hero">
      <div>
        <div className="pp-vu__hero-title">{t("What's new")}</div>
        {builtAt && (
          <div className="pp-vu__hero-date">
            {new Date(builtAt).toLocaleDateString(lang === 'id' ? 'id-ID' : undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Two complementary deploy popups, driven by /version.json (stamped by the
 * deploy script from deploy/release-notes.json; says "dev" locally so neither
 * ever fires in development):
 *
 * 1. "What's new" — on load, when this version's release notes haven't been
 *    shown to this user yet (tracked in localStorage).
 * 2. "New version available" — while the tab stays open, polling detects that
 *    a newer build was deployed and asks the user to refresh.
 */
export default function VersionUpdateWatcher() {
  const { t, lang } = useI18n();
  const [update, setUpdate] = useState(null); // refresh prompt: { version, notes, builtAt }
  const [updateOpen, setUpdateOpen] = useState(false);
  const [whatsNew, setWhatsNew] = useState(null); // post-load notes: { version, notes, builtAt }
  const baselineRef = useRef(null);
  const lastCheckRef = useRef(0);
  const dismissedRef = useRef({ version: null, at: 0 });

  const check = useCallback(async () => {
    const now = Date.now();
    if (now - lastCheckRef.current < MIN_CHECK_GAP_MS) return;
    lastCheckRef.current = now;

    let data;
    try {
      data = await fetchVersion();
    } catch {
      return;
    }
    if (!data || data.version === 'dev') return;

    if (baselineRef.current === null) {
      baselineRef.current = data.version;
      // First check after load: show this version's notes if the user hasn't seen them.
      const notes = cleanNotes(data.notes);
      let seen = null;
      try { seen = localStorage.getItem(SEEN_VERSION_KEY); } catch { /* storage unavailable */ }
      if (seen !== data.version && notes.length > 0) {
        setWhatsNew({ version: data.version, notes, builtAt: data.builtAt || null });
      } else if (seen !== data.version) {
        try { localStorage.setItem(SEEN_VERSION_KEY, data.version); } catch { /* ignore */ }
      }
      return;
    }
    if (data.version === baselineRef.current) return;

    const dismissed = dismissedRef.current;
    const snoozed = dismissed.version === data.version && now - dismissed.at < RENOTIFY_AFTER_MS;
    setUpdate({ version: data.version, notes: cleanNotes(data.notes), builtAt: data.builtAt || null });
    if (!snoozed) setUpdateOpen(true);
  }, []);

  useEffect(() => {
    check();
    const timer = setInterval(check, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check]);

  const acknowledgeWhatsNew = () => {
    if (whatsNew?.version) {
      try { localStorage.setItem(SEEN_VERSION_KEY, whatsNew.version); } catch { /* ignore */ }
    }
    setWhatsNew(null);
  };

  const later = () => {
    dismissedRef.current = { version: update?.version || null, at: Date.now() };
    setUpdateOpen(false);
  };

  const refresh = () => {
    // The reload lands on the new version; don't re-show its notes as "what's new".
    if (update?.version) {
      try { localStorage.setItem(SEEN_VERSION_KEY, update.version); } catch { /* ignore */ }
    }
    window.location.reload();
  };

  // Refresh prompt wins: it's actionable, and the reload marks notes as seen anyway.
  if (update) {
    return (
      <Dialog
        open={updateOpen}
        onClose={later}
        size="md"
        className="pp-vu"
        title={t('A new version is available')}
        description={t('We just shipped an update. Refresh to make sure everything works as intended.')}
        footer={
          <>
            <Button variant="ghost" onClick={later}>{t('Later')}</Button>
            <Button variant="primary" leadingIcon={<Icon.Refresh size={16} />} onClick={refresh}>
              {t('Refresh now')}
            </Button>
          </>
        }
      >
        {update.notes.length > 0 ? (
          <ReleaseNotes notes={update.notes} lang={lang} />
        ) : (
          <p className="pp-vu__text">
            {t('If you are in the middle of something, save your work first — refreshing discards unsaved changes.')}
          </p>
        )}
      </Dialog>
    );
  }

  if (whatsNew) {
    return (
      <Dialog
        open
        onClose={acknowledgeWhatsNew}
        size="lg"
        className="pp-vu"
        footer={
          <Button variant="primary" onClick={acknowledgeWhatsNew}>{t('Got it')}</Button>
        }
      >
        <Hero t={t} lang={lang} builtAt={whatsNew.builtAt} />
        <p className="pp-vu__intro">{t("You're on the latest version — here's what changed in the most recent update.")}</p>
        <ReleaseNotes notes={whatsNew.notes} lang={lang} />
      </Dialog>
    );
  }

  return null;
}
